/**
 * classifyKolAccount — KOL 账号分类（project vs person vs unsure）
 *
 * 用多信号加权评分模型替代之前对 RootData accountType 字段的盲目信任。
 *
 * 信号源（按强度加权）：
 *   1. ffMatch（fundraise.fun 融资记录）—— 几乎 100% 是项目
 *   2. AI bio classification（DeepSeek 看 Twitter 简介判断）
 *   3. Bio 文本模式（第一人称 we/our → project；I/my → person；at @某项目 → person）
 *   4. 用户名/Handle 模式（_official / labs / protocol / 数字 → project；短人名 / .eth → person）
 *   5. Display name 模式（CamelCase 单词 / 全大写 → project；姓+名 / 中文 2-4 字 → person）
 *   6. RootData accountType 字段（弱信号，作为兜底）
 *   7. 用户手动标记（userTags）—— 最高优先级，直接 override
 *
 * 输出三态：
 *   { type: 'project' | 'person' | 'unsure', confidence: 0..1, signals: {...} }
 *
 * confidence 计算：|总分| / 满分（10）。
 * 阈值：|总分| >= 3 才会判定 project/person，否则 'unsure'。
 */

// ── 项目特征模式 ──────────────────────────────────────────────
const PROJECT_NAME_PATTERNS = [
  /\b(labs?|protocol|network|finance|dao|capital|ventures|foundation|studio|research|ai|tech|swap|dex|defi|bridge|vault|node|exchange|markets?|treasury|stake|chain|hub|pool|fund|registry|oracle|index)\b/i,
];

const PROJECT_HANDLE_PATTERNS = [
  /_official$/i,                     // _official 后缀
  /(labs?|protocol|network|finance|dao|swap|dex|bridge|vault|exchange|defi)$/i,
  /^[a-z]+\d{3,}$/i,                 // labs1234（项目常用编号）
];

const PROJECT_BIO_TOKENS = [
  // 第一人称复数 — 项目常用
  /\bwe(?:'re|\sare)?\b/i,
  /\bour\s+(mission|platform|protocol|product|team|community|infrastructure)/i,
  // 项目动词
  /\bbuilding\s+(the|a|an|on|for)/i,
  /\b(launching|launched|powered\s+by|backed\s+by|incubated\s+by)/i,
  /\b(decentralized|web3|crypto|blockchain|on-?chain|layer\s*[12])\s+(platform|infrastructure|protocol|network|exchange|wallet|aggregator)/i,
  // 项目角色名词
  /\b(L1|L2|DEX|CEX|DAO|DePIN|MEV|RWA|GameFi|SocialFi|NFTfi)\b/,
  // 中文项目特征
  /(协议|平台|项目|网络|生态|公链|去中心化交易所|借贷协议|跨链桥)/,
];

// ── 个人特征模式 ──────────────────────────────────────────────
const PERSON_NAME_PATTERNS = [
  /^[A-Z][a-z]+\s+[A-Z][a-z]+$/,    // First Last（Vitalik Buterin）
  /^[一-龥]{2,4}$/,          // 中文 2-4 字
  /\.eth$/i,                         // ENS 域名
  /\.(sol|bnb|lens|base|arb|btc)$/i, // chain handle
];

const PERSON_HANDLE_PATTERNS = [
  /\.eth$/i,
  /\.(sol|bnb|lens|base|arb)$/i,
];

const PERSON_BIO_TOKENS = [
  // 第一人称单数 — 个人常用
  /\bi(?:'m|\sam)\b/i,
  /\bmy\s+(views|opinions?|tweets|thoughts|takes|work)\b/i,
  // 角色介绍 — 通常带有"at <某项目>"或"of <某项目>"
  /\b(founder|co-?founder|ceo|cto|head\s+of|partner|director|engineer|researcher|developer|investor|advisor|contributor|advocate|builder|writer|analyst)\s+(of|at|@)/i,
  // 角色名词单独出现也算（弱信号）
  /\b(co-?founder|founder|engineer|researcher|developer|investor|partner|director|analyst)\b/i,
  /\bex[-\s](?:google|meta|coinbase|binance|paradigm|a16z|fed|jp\s*morgan|goldman)/i,
  /\b(views|tweets|opinions?)\s+are\s+(my|mine)\s+own\b/i,
  /\b(personal|individual)\s+account\b/i,
  // 中文个人特征
  /(合伙人|创始人|联合创始人|联创|投资人|分析师|研究员|工程师|开发者|博士|教授)/,
];

const PERSON_LOWFI_TOKENS = [
  // 弱信号 — 单纯出现这些词
  /\b(thinker|writer|builder|dev|engineer|researcher|trader)\b/i,
];

// ── 文本辅助 ────────────────────────────────────────────────
function safeText(...parts) {
  return parts.filter(Boolean).join(' ').toString();
}

function countMatches(text, patterns) {
  return patterns.reduce((s, re) => s + (re.test(text) ? 1 : 0), 0);
}

// ── 主分类器 ───────────────────────────────────────────────
/**
 * @param {Object} account
 * @param {string} [account.username]    — Twitter handle (无 @)
 * @param {string} [account.name]        — 显示名
 * @param {string} [account.bio]         — Twitter bio 原文（可能为空）
 * @param {string} [account.aiBio]       — DeepSeek 生成的简介（中文）
 * @param {boolean} [account.aiIsProject] — DeepSeek 判定 isProject
 * @param {string} [account.accountType] — RootData 的 'project'/'person' 字段
 * @param {boolean} [account.ffMatch]    — 与 fundraise.fun 融资记录匹配
 * @param {number} [account.followers]   — 粉丝数
 * @param {number} [account.following]   — 关注数
 * @param {Array}  [account.tags]        — RootData 标签
 * @param {string} [account.userTag]     — 用户手动标注（'Person' 直接 person）
 * @returns {{ type: 'project'|'person'|'unsure', confidence: number, signals: object }}
 */
export function classifyKolAccount(account = {}) {
  // ── 0. 用户手动覆盖 — 最高优先级 ─────────────────────
  if (account.userTag === 'Person') {
    return { type: 'person', confidence: 1, signals: { userTag: -10 } };
  }
  if (account.userTag && account.userTag !== 'Person') {
    return { type: 'project', confidence: 1, signals: { userTag: 10 } };
  }

  const signals = {};
  let score = 0; // 正 → project，负 → person

  // ── 1. ffMatch — 强项目信号 ──────────────────────────
  if (account.ffMatch) {
    signals.ffMatch = 4;
    score += 4;
  }

  // ── 2. AI bio classification — 强信号 ─────────────────
  if (account.aiIsProject === true) {
    signals.aiBio = 3;
    score += 3;
  } else if (account.aiIsProject === false) {
    signals.aiBio = -3;
    score -= 3;
  }

  // ── 2.5. AI role 字段（P2.B 结构化输出）— 强信号 ─────
  // 项目类 role: Protocol/DEX/L1/L2/Wallet/Infra/DAO/Fund/Foundation/Tool
  // 个人类 role: CEO/Founder/CTO/Engineer/Researcher/Analyst/Investor/Influencer/Trader/Builder/AnonDev
  if (account.aiRole) {
    const PROJECT_ROLES = /^(protocol|dex|cex|l1|l2|wallet|infra|dao|fund|foundation|tool|exchange|bridge|aggregator|marketplace)$/i;
    const PERSON_ROLES = /^(ceo|founder|co-?founder|cto|engineer|researcher|analyst|investor|influencer|trader|builder|anondev|developer|partner|director|advisor)$/i;
    if (PROJECT_ROLES.test(account.aiRole)) {
      signals.aiRoleProject = 2;
      score += 2;
    } else if (PERSON_ROLES.test(account.aiRole)) {
      signals.aiRolePerson = -2;
      score -= 2;
    }
  }

  // ── 3. Bio 文本模式（综合 Twitter bio + AI bio）───────
  const bioText = safeText(account.bio, account.aiBio);
  if (bioText) {
    const projectBioHits = countMatches(bioText, PROJECT_BIO_TOKENS);
    const personBioHits  = countMatches(bioText, PERSON_BIO_TOKENS);
    if (projectBioHits > 0) {
      signals.bioProject = projectBioHits;
      score += Math.min(projectBioHits, 3); // 封顶 +3
    }
    if (personBioHits > 0) {
      signals.bioPerson = personBioHits;
      score -= Math.min(personBioHits, 3); // 封顶 -3
    }
    // "at @某项目" / "of @某项目" 强个人信号
    if (/\b(at|of|head\s+of)\s+@?[a-z][a-z0-9_]{2,}/i.test(bioText)) {
      signals.bioEmployee = -2;
      score -= 2;
    }
  }

  // ── 4. Handle 模式 ──────────────────────────────────
  const username = (account.username || '').toLowerCase();
  if (username) {
    if (PROJECT_HANDLE_PATTERNS.some(re => re.test(username))) {
      signals.handleProject = 2;
      score += 2;
    }
    if (PERSON_HANDLE_PATTERNS.some(re => re.test(username))) {
      signals.handlePerson = -2;
      score -= 2;
    }
  }

  // ── 5. Display name 模式 ────────────────────────────
  const name = account.name || '';
  if (name) {
    if (PROJECT_NAME_PATTERNS.some(re => re.test(name))) {
      signals.nameProject = 1;
      score += 1;
    }
    if (PERSON_NAME_PATTERNS.some(re => re.test(name))) {
      signals.namePerson = -2;
      score -= 2;
    }
    // CamelCase 单词且不含空格（SignalPlus / OpenAI）
    if (/^[A-Z][a-z]+[A-Z][a-z]+$/.test(name) && !name.includes(' ')) {
      signals.nameCamelCase = 1;
      score += 1;
    }
  }

  // ── 6. RootData accountType — 弱兜底 ────────────────
  if (account.accountType === 'project') {
    signals.rdProject = 1;
    score += 1;
  } else if (account.accountType === 'person') {
    signals.rdPerson = -1;
    score -= 1;
  }

  // ── 7. 关注比（弱信号）— 项目通常 followers >> following ─
  const followers = Number(account.followers) || 0;
  const following = Number(account.following) || 0;
  if (followers > 5000 && following < 100 && followers > following * 50) {
    signals.ratioProject = 1;
    score += 1;
  }

  // ── 决策 ───────────────────────────────────────────
  // 满分大致 ±10：ffMatch(4) + aiBio(3) + bio(3) + handle(2) + name(2) + rd(1) + ratio(1)
  const MAX_SCORE = 10;
  const confidence = Math.min(Math.abs(score) / MAX_SCORE, 1);

  let type;
  if (score >= 3) type = 'project';
  else if (score <= -3) type = 'person';
  else type = 'unsure';

  return { type, confidence, signals, score };
}

/**
 * Helper：直接判断是否项目（兼容旧 isEffectiveProject 调用方）
 */
export function isProjectAccount(account) {
  return classifyKolAccount(account).type === 'project';
}

/**
 * Helper：判断是否个人
 */
export function isPersonAccount(account) {
  return classifyKolAccount(account).type === 'person';
}
